import { z } from "zod";

// TES-1587 (privacy policy §4.2, Zeinab/Emelie 2026-10-23) — the explicit
// consent (GDPR art. 6.1 a + 9.2 a) the web quiz asks for BEFORE its first
// question. The answers include health data (weight, height, conditions), and
// a visitor who has not signed in with BankID has no patient relationship
// that could carry them (§4.1), so consent is the only basis.
//
// The same evidence pattern as TES-433 and TES-1106: the lead stores which
// wording she saw (a version id) and when she ticked it.

/**
 * Every wording that has ever been shown, by version. The Swedish text is the
 * one Simon approved 2026-10-23, verbatim; it is the evidence of what a
 * stored version means. The final wording (Ö-923, Zeinab/Emelie) replaces it
 * by ADDING a version here and moving QUIZ_CONSENT_VERSION to it, never by
 * editing an existing entry: a stored lead points at its version for good.
 */
export const QUIZ_CONSENT_TEXTS = {
  "2026-10-23":
    "Jag samtycker till att Ella behandlar mina svar, inklusive hälsouppgifter, för att visa om Ellas behandling kan passa mig. Samtycket kan återkallas när som helst, och då raderas svaren.",
} as const;

export type QuizConsentVersion = keyof typeof QUIZ_CONSENT_TEXTS;

/** The wording in force, shown by the web quiz today. */
export const QUIZ_CONSENT_VERSION: QuizConsentVersion = "2026-10-23";

export function isQuizConsentVersion(v: unknown): v is QuizConsentVersion {
  return (
    typeof v === "string" &&
    Object.prototype.hasOwnProperty.call(QUIZ_CONSENT_TEXTS, v)
  );
}

// What the quiz sends with its answers. `consentedAt` is when she ticked the
// box (the client's clock; the API never stores a time later than its own).
export const quizConsentSchema = z.object({
  version: z.string().refine(isQuizConsentVersion, {
    message: "unknown consent version",
  }),
  consentedAt: z.string().datetime(),
});
export type QuizConsent = z.infer<typeof quizConsentSchema>;

/**
 * The stable error code POST /public-api/v1/leads answers with when a
 * submission carries no valid consent: missing, malformed, or a version the
 * API does not know. A web tab opened before the consent step shipped posts
 * without it; the current web shows "ladda om sidan" on this code.
 */
export const QUIZ_CONSENT_REQUIRED_ERROR = "consent_required";
